import { getAccessToken } from "../../utils/auth";
import React, { useEffect, useState } from "react";
import axios from "../../utils/api";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  IconButton,
  Avatar,
  Skeleton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useNavigate } from "react-router-dom";

const UserList = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEmails = async () => {
      try {
        const response = await axios.get("/get-user-emails", {
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
          },
        });
        setEmails(response.data ?? []);
      } catch (error) {
        console.error("Error fetching emails:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmails();
  }, []);

  const encodeEmail = (email) => {
    return encodeURIComponent(btoa(email));
  };

  const getInitials = (email) => {
    const local = email.split("@")[0] || "";
    if (local.length >= 2) return local.slice(0, 2).toUpperCase();
    return local.toUpperCase() || "?";
  };

  const accent = "#0d47a1";
  const accentLight = "rgba(13, 71, 161, 0.08)";

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#fafbfc",
          py: { xs: 2, md: 4 },
          px: { xs: 2, md: 4 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="text" width={320} height={40} />
          </Box>
          <Grid container spacing={4}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item key={i} xs={12} sm={6} md={4}>
                <Skeleton
                  variant="rounded"
                  height={100}
                  sx={{ borderRadius: 3 }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#fafbfc",
        py: { xs: 2, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            mb: 3,
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            size="medium"
            sx={{
              bgcolor: "white",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              "&:hover": {
                bgcolor: "#f5f5f5",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#1a1a2e",
                fontSize: { xs: "1.25rem", md: "1.5rem" },
              }}
            >
              Robot users
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
              Select a user to view and manage their robots
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        {emails.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              px: 2,
              bgcolor: "white",
              borderRadius: 3,
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            }}
          >
            <PersonOutlineRoundedIcon
              sx={{ fontSize: 56, color: "#cbd5e1", mb: 2 }}
            />
            <Typography variant="h6" sx={{ color: "#475569", fontWeight: 600 }}>
              No users found
            </Typography>
            <Typography variant="body2" sx={{ color: "#94a3b8", mt: 1 }}>
              There are no robot users to display yet.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {emails.map((email, index) => (
              <Grid item key={index} xs={12} sm={6} md={4}>
                <Card
                  onClick={() => navigate(`/robots/${encodeEmail(email)}`)}
                  elevation={0}
                  sx={{
                    cursor: "pointer",
                    borderRadius: 3,
                    overflow: "hidden",
                    bgcolor: "white",
                    boxShadow: "none",
                    border: "1px solid rgba(0,0,0,0.06)",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    "&:before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(to right, #0048e6, #9C15F7, rgba(255, 75, 248, 0.3))",
                      opacity: 0,
                      zIndex: 0,
                    },
                    "&:hover": {
                      "&:before": {
                        opacity: 0.28,
                      },
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2.5,
                      py: 3.5,
                      px: 3,
                      "&:last-child": { pb: 3.5 },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: accentLight,
                        color: accent,
                        fontWeight: 700,
                        fontSize: "1.15rem",
                      }}
                    >
                      {getInitials(email)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          fontSize: "1.05rem",
                          color: "#1e293b",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {email}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.875rem",
                          color: "#94a3b8",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
                        View robots
                      </Typography>
                    </Box>
                    <ChevronRightRoundedIcon
                      sx={{ color: accent, opacity: 0.6, fontSize: 28 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default UserList;